// Copyright 2026 TokenLat. Licensed under the Apache License, Version 2.0.
import { beginAuthorization } from '@coze-foundation/account-adapter';
import { I18n } from '@coze-arch/i18n';
import { Button } from '@coze-arch/coze-design';

import {
  AccountError,
  formatCredits,
  type AccountConfig,
  type AccountOptions,
  type AccountKey,
  type AccountBalance,
} from './tokenlat-api';

import styles from './tokenlat-account.module.less';
export const t = (key: string) => I18n.t(`tl_account_${key}`);

export function AccountNotice({ error }: { error: unknown }) {
  if (!error) {
    return null;
  }
  const code =
    error instanceof AccountError ? error.code : 'account_unavailable';
  const authorize =
    code === 'additional_authorization_required' ||
    code === 'authorization_required';
  const known = [
    'key_creation_unconfirmed',
    'account_service_disabled',
    'access_denied',
    'invalid_authorization',
    'invalid_amount',
    'invalid_expiry',
  ];
  return (
    <div role="alert" className={styles.error}>
      <p>
        {t(
          authorize
            ? 'reauthorize'
            : known.includes(code)
              ? code
              : 'account_unavailable',
        )}
      </p>
      {authorize ? (
        <Button
          onClick={() =>
            beginAuthorization(
              window.location.pathname + window.location.search,
              'login',
            )
          }
        >
          {t('authorize')}
        </Button>
      ) : null}
    </div>
  );
}

export function PlatformLink({
  config,
  path,
  children,
}: {
  config?: AccountConfig;
  path: '/keys' | '/wallet' | '/usage-logs';
  children: React.ReactNode;
}) {
  return config?.platform_origin ? (
    <a
      href={`${config.platform_origin}${path}`}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ) : null;
}

export function legacyAmount(
  balance: Pick<
    AccountBalance,
    | 'quota'
    | 'quota_per_unit'
    | 'quota_display_type'
    | 'usd_exchange_rate'
    | 'custom_currency_exchange_rate'
    | 'custom_currency_symbol'
  >,
): string {
  const { quota } = balance;
  const unit = balance.quota_per_unit;
  if (typeof quota !== 'number' || typeof unit !== 'number' || unit <= 0) {
    return '—';
  }
  if (balance.quota_display_type === 'TOKENS') {
    return `${quota.toLocaleString()} quota`;
  }
  let ratio = 1;
  let symbol = '$';
  if (balance.quota_display_type === 'CNY') {
    ratio = balance.usd_exchange_rate ?? 1;
    symbol = '¥';
  } else if (balance.quota_display_type === 'CUSTOM') {
    ratio = balance.custom_currency_exchange_rate ?? 1;
    symbol = balance.custom_currency_symbol ?? '';
  }
  return `${symbol}${((quota / unit) * ratio).toLocaleString(undefined, { maximumFractionDigits: 6 })}`;
}

export function KeyBudget({
  item,
  options,
}: {
  item: AccountKey;
  options: AccountOptions;
}) {
  if (options.billing_mode === 'unified') {
    if (!item.credit_budget) {
      return <span>{t('budget_unavailable')}</span>;
    }
    return (
      <span className={styles['budget-values']}>
        {item.credit_budget.unlimited
          ? t('unlimited')
          : `${formatCredits(item.credit_budget.available_units, options.credit_scale)} ${t('credits')}`}
        <span className={styles.muted}>
          {t('used')}:{' '}
          {formatCredits(
            item.credit_budget.consumed_units,
            options.credit_scale,
          )}{' '}
          {t('credits')}
        </span>
        <span className={styles.muted}>
          {t('processing')}:{' '}
          {formatCredits(
            item.credit_budget.reserved_units,
            options.credit_scale,
          )}{' '}
          {t('credits')}
        </span>
        {!item.credit_budget.unlimited &&
        item.credit_budget.total_limit_units !== undefined ? (
          <span className={styles.muted}>
            {t('total_limit')}:{' '}
            {formatCredits(
              item.credit_budget.total_limit_units,
              options.credit_scale,
            )}{' '}
            {t('credits')}
          </span>
        ) : null}
      </span>
    );
  }
  return (
    <span className={styles['budget-values']}>
      {item.unlimited_quota
        ? t('unlimited')
        : legacyAmount({ ...options, quota: item.remain_quota })}
      <span className={styles.muted}>
        {t('used')}: {legacyAmount({ ...options, quota: item.used_quota })}
      </span>
      {!item.unlimited_quota && item.used_quota !== undefined ? (
        <span className={styles.muted}>
          {t('total_limit')}:{' '}
          {legacyAmount({
            ...options,
            quota: item.used_quota + item.remain_quota,
          })}
        </span>
      ) : null}
    </span>
  );
}
